import { Box, FormHelperText, Link } from '@mui/material';
import type { ReactNode } from 'react';
import { staleEditTheirs, type StaleEdit } from '../../../utils/staleEdit';
import { TonePill } from '../TonePill';
import { staleClashRingSx } from './staleClash';
import type { StaleConflict } from './useStaleEdit';

export interface StaleNoteOptions<V> {
  /** The field's name as the form shows it; gives "Use …'s" an accessible name. */
  label?: string;
  /** How the other person's value reads in "Alex Kim set {value}". */
  format?: (value: V) => string;
  /** Puts their value into the field; the caller also calls `settle(field)`. */
  onUseTheirs?: () => void;
}

/**
 * Board C's per-field note, the one place it is worded:
 * - only they changed it → an info pill, "Updated by {name}";
 * - only the reader changed it → an accent pill, "Your change";
 * - both did → a warning pill, "You both changed this", then
 *   "{name} set {value} · Use {name}'s".
 */
export function StaleNoteContent<V>({
  who,
  status,
  theirs,
  label,
  format,
  onUseTheirs,
}: {
  who: StaleEdit;
  status: 'theirs' | 'mine' | 'clash';
  theirs: V;
} & StaleNoteOptions<V>) {
  if (status === 'theirs') {
    return (
      <TonePill
        tone="info"
        label={who.byYou ? 'Updated in your other tab' : `Updated by ${who.changedBy ?? 'someone else'}`}
      />
    );
  }
  if (status === 'mine') return <TonePill tone="accent" label="Your change" />;
  const shown = format ? format(theirs) : theirs == null || theirs === '' ? 'nothing' : String(theirs);
  const subject = who.byYou ? 'Your other tab' : (who.changedBy ?? 'Someone else');
  const possessive = staleEditTheirs(who);
  return (
    <Box component="span" sx={{ display: 'inline-flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}>
      <TonePill tone="warn" label={who.byYou ? 'Both saves changed this' : 'You both changed this'} />
      <span>
        {subject} set <Box component="b" sx={{ fontWeight: 600, color: 'text.primary' }}>{shown}</Box>
      </span>
      {onUseTheirs && (
        <>
          <span aria-hidden>·</span>
          <Link
            component="button"
            type="button"
            onClick={onUseTheirs}
            aria-label={label ? `Use ${possessive} value for ${label}` : undefined}
            sx={{ fontWeight: 600, fontSize: 'inherit' }}
          >
            Use {possessive}
          </Link>
        </>
      )}
    </Box>
  );
}

/**
 * Wraps a control that is not a text field (a select, a toggle, an editor) with
 * the note beneath it, ringed when both people changed it. A text field takes
 * `staleFieldProps` instead, which draws the same ring on its own border.
 */
export function StaleNote<T extends Record<string, unknown>, K extends keyof T & string>({
  conflict,
  field,
  children,
  ...options
}: {
  conflict: StaleConflict<T> | null | undefined;
  field: K;
  children: ReactNode;
} & StaleNoteOptions<T[K]>) {
  const status = conflict?.status[field];
  const noted: 'theirs' | 'mine' | 'clash' | null =
    conflict && status && status !== 'same' ? (status as 'theirs' | 'mine' | 'clash') : null;
  // One wrapper in every state, so the control inside keeps its identity (an
  // editor must not remount, and lose its text, the moment a conflict opens or
  // closes); with nothing to say it takes no part in layout.
  return (
    <Box sx={noted === 'clash' ? staleClashRingSx : noted ? undefined : { display: 'contents' }}>
      {children}
      {conflict && noted && (
        <FormHelperText component="div" sx={{ mx: noted === 'clash' ? 0 : 1.75, mt: 0.5 }}>
          <StaleNoteContent who={conflict.who} status={noted} theirs={conflict.theirs[field]} {...options} />
        </FormHelperText>
      )}
    </Box>
  );
}

export default StaleNote;
